/*
 * Convert to Infographic — source content + region-target extraction.
 *
 * Reads what the backend should turn into an infographic, and — parity port
 * of the DecKorate POC's mode decision — whether the result should merge
 * into the selected shape's own footprint ("region" mode) or land as a
 * brand-new slide ("full_slide" mode):
 *   1. A selected table shape -> {type:"table"} content, region = that
 *      table's own frame, shapeNames = [that shape's name].
 *   2. Other selected shape(s) with text -> {type:"text"} content, region =
 *      the union bounding box of the selection, shapeNames = their names.
 *   3. A selection with neither (e.g. a chart or picture, which carry no
 *      readable text) -> falls through to the whole-slide case below,
 *      full_slide mode — matching the POC exactly: charts/images are never
 *      specially read, they just degrade to whatever text sits elsewhere on
 *      the slide.
 *   4. Nothing selected -> the whole current slide's shape text, full_slide
 *      mode.
 * Also reads the deck's live theme colours and slide size (EMU), best-effort,
 * so the generated infographic matches the deck instead of a generic default.
 */

import { EMU_PER_POINT } from "../../../const";
import type { SlideDimensions, ThemeColors } from "../../create-slide/service/slideGenerationApi";
import { readThemeColors } from "../../create-slide/hooks/themeColors";
import { hasPpt, ppt } from "../../../utils/formatUtils";
import {
  EMPTY_CONTENT_MESSAGE,
  logInfographicError,
  MAX_INFOGRAPHIC_TEXT_CHARS,
  type InfographicContent,
  type InfographicMode,
} from "./infographicApi";
import type { RegionGeometryEmu } from "./infographicRegionMerge";

/** 16:9 default (matches DeckorateAPI's SlideDimensions default) — used only
 *  when the live slide size can't be read. */
const DEFAULT_DIMENSIONS: SlideDimensions = { cx: 12192000, cy: 6858000 };

export const POWERPOINT_ONLY_MESSAGE = "This tool works only in PowerPoint.";

export interface InfographicRegionTarget {
  readonly geometry: RegionGeometryEmu;
  readonly shapeNames: readonly string[];
}

export interface InfographicExtraction {
  readonly content: InfographicContent;
  readonly mode: InfographicMode;
  /** Non-null only when `mode === "region"`. */
  readonly region: InfographicRegionTarget | null;
}

function shapeTypeOf(shape: PowerPoint.Shape): string {
  return String((shape as unknown as { type?: string }).type ?? "").toLowerCase();
}

interface RawTableCell {
  body: { textRange: PowerPoint.TextRange };
}
interface RawTableShape {
  rowCount: number;
  columnCount: number;
  getCell(row: number, col: number): RawTableCell;
  load(props: string): void;
}
function shapeTable(shape: PowerPoint.Shape): RawTableShape {
  return (shape as unknown as { table: RawTableShape }).table;
}

function pointsToRegionEmu(left: number, top: number, width: number, height: number): RegionGeometryEmu {
  return {
    x: Math.round(left * EMU_PER_POINT),
    y: Math.round(top * EMU_PER_POINT),
    cx: Math.round(width * EMU_PER_POINT),
    cy: Math.round(height * EMU_PER_POINT),
  };
}

/** Union bounding box (points) of every selected shape, converted to EMU. */
function unionRegionEmu(shapes: readonly PowerPoint.Shape[]): RegionGeometryEmu {
  let minLeft = Infinity;
  let minTop = Infinity;
  let maxRight = -Infinity;
  let maxBottom = -Infinity;
  for (const s of shapes) {
    minLeft = Math.min(minLeft, s.left);
    minTop = Math.min(minTop, s.top);
    maxRight = Math.max(maxRight, s.left + s.width);
    maxBottom = Math.max(maxBottom, s.top + s.height);
  }
  return pointsToRegionEmu(minLeft, minTop, maxRight - minLeft, maxBottom - minTop);
}

/** Reads a single table shape into a plain string grid, within an
 *  already-open PowerPoint.run batch (mirrors SpeakerNotes's table reader). */
async function readTableGrid(
  context: PowerPoint.RequestContext,
  tableShape: PowerPoint.Shape,
): Promise<string[][]> {
  const tbl = shapeTable(tableShape);
  tbl.load("rowCount,columnCount");
  await context.sync();

  const ranges: Array<{ row: number; col: number; range: PowerPoint.TextRange }> = [];
  for (let row = 0; row < tbl.rowCount; row++) {
    for (let col = 0; col < tbl.columnCount; col++) {
      try {
        const range = tbl.getCell(row, col).body.textRange;
        range.load("text");
        ranges.push({ row, col, range });
      } catch (cellErr) {
        // merged/malformed cell — left blank below
        logInfographicError("infographic-table-cell-read-failed", cellErr);
      }
    }
  }
  await context.sync();

  const grid: string[][] = Array.from({ length: tbl.rowCount }, () => new Array<string>(tbl.columnCount).fill(""));
  for (const { row, col, range } of ranges) {
    grid[row]![col] = range.text ?? "";
  }
  return grid;
}

/** Reads the text of each shape into one array, tolerating shapes without a
 *  textFrame (e.g. a chart or picture) by skipping them. */
async function readShapesText(
  context: PowerPoint.RequestContext,
  shapes: readonly PowerPoint.Shape[],
): Promise<string[]> {
  const texts: string[] = [];
  for (const shape of shapes) {
    try {
      const tf = shape.textFrame;
      tf.load("textRange/text");
      await context.sync();
      if (tf.textRange?.text?.trim()) texts.push(tf.textRange.text.trim());
    } catch (shapeErr) {
      // shape has no textFrame (e.g. a chart/picture) — skip it
      logInfographicError("infographic-shape-text-read-failed", shapeErr);
    }
  }
  return texts;
}

/** Reads every shape's text on the current slide (used when nothing is selected). */
async function readCurrentSlideBlocks(context: PowerPoint.RequestContext): Promise<string[]> {
  const selectedSlides = context.presentation.getSelectedSlides();
  selectedSlides.load("items");
  await context.sync();
  const slide = selectedSlides.items[0];
  if (!slide) return [];

  slide.shapes.load("items");
  await context.sync();
  return readShapesText(context, slide.shapes.items);
}

/**
 * Builds the InfographicContent + mode/region payload from the live
 * PowerPoint selection. Throws EMPTY_CONTENT_MESSAGE when there's genuinely
 * nothing to convert — mirrors Fix Alignment's "nothing to align" guard so
 * the caller shows one clear message instead of letting the API 422.
 */
export async function extractInfographicContent(): Promise<InfographicExtraction> {
  if (!hasPpt()) throw new Error(POWERPOINT_ONLY_MESSAGE);

  return ppt().run(async (context) => {
    const selected = context.presentation.getSelectedShapes();
    selected.load("items/name,items/left,items/top,items/width,items/height,items/type");
    await context.sync();

    if (selected.items.length > 0) {
      const tableShape = selected.items.find((s) => shapeTypeOf(s) === "table");
      if (tableShape) {
        const grid = await readTableGrid(context, tableShape);
        const [headers, ...rows] = grid;
        if (headers && grid.length > 0) {
          return {
            content: { type: "table", table: { headers, rows } },
            mode: "region",
            region: {
              geometry: pointsToRegionEmu(tableShape.left, tableShape.top, tableShape.width, tableShape.height),
              shapeNames: [tableShape.name],
            },
          } satisfies InfographicExtraction;
        }
      }

      const texts = await readShapesText(context, selected.items);
      const text = texts.join("\n").trim();
      if (text) {
        return {
          content: { type: "text", text: text.slice(0, MAX_INFOGRAPHIC_TEXT_CHARS) },
          mode: "region",
          region: {
            geometry: unionRegionEmu(selected.items),
            shapeNames: selected.items.map((s) => s.name),
          },
        } satisfies InfographicExtraction;
      }
      // Selection had no usable table/text (e.g. a chart or picture) — fall
      // through to the whole-slide case below, exactly like the legacy POC.
    }

    const blocks = await readCurrentSlideBlocks(context);
    if (blocks.length === 0) throw new Error(EMPTY_CONTENT_MESSAGE);
    return {
      content: { type: "slide", slide: { title: null, blocks } },
      mode: "full_slide",
      region: null,
    } satisfies InfographicExtraction;
  });
}

/** Reads the active slide's size in EMU, falling back to the 16:9 default on
 *  any failure — mirrors themeColors.ts's "degrade to default, never throw". */
export async function readSlideDimensionsEmu(): Promise<SlideDimensions> {
  if (!hasPpt()) return DEFAULT_DIMENSIONS;
  try {
    return await ppt().run(async (context) => {
      const pageSetup = context.presentation.pageSetup;
      pageSetup.load("slideWidth,slideHeight");
      await context.sync();
      const widthPts = pageSetup.slideWidth;
      const heightPts = pageSetup.slideHeight;
      if (!widthPts || !heightPts) return DEFAULT_DIMENSIONS;
      return { cx: Math.round(widthPts * EMU_PER_POINT), cy: Math.round(heightPts * EMU_PER_POINT) };
    });
  } catch (dimsErr) {
    logInfographicError("infographic-slide-dimensions-read-failed", dimsErr);
    return DEFAULT_DIMENSIONS;
  }
}

/** Reads the deck's live theme colours, best-effort (never throws). */
export async function readDeckThemeColors(): Promise<ThemeColors | null> {
  return readThemeColors();
}
